import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import type {
  AttendanceStatus, AttendanceTeamSummary, FasAttendant, ImportedMember, MemberAttendance,
  CryptoAgenda, CryptoSale, CryptoSaleInput,
  CatalogRefresh, CheckoutLink, KirvanoAccount, CheckoutLinkInput, LinkAccess, LinkGroup, Dashboard, GasSummary, GoalsResult, Lead, LeadDepositRanking, MemberDepositRanking, MemberLinks, Metrics, Page, Ranking, Receipt, ReceiptPage, ReviewResult, ReviewStatus, User,
} from '@/lib/types';

/** Chaves de cache centralizadas — evita invalidação por string solta. */
export const keys = {
  dashboard: (params: unknown) => ['dashboard', params] as const,
  /** Debaixo de "dashboard": lançar qualquer comprovante invalida os dois. */
  gas: (params: unknown) => ['dashboard', 'gas', params] as const,
  metrics: (params: unknown) => ['metrics', params] as const,
  users: (params: unknown) => ['users', params] as const,
  leads: (params: unknown) => ['leads', params] as const,
  receipts: (params: unknown) => ['receipts', params] as const,
  goals: (params: unknown) => ['goals', params] as const,
  ranking: (params: unknown) => ['ranking', params] as const,
  /** Debaixo de "ranking": lançar um comprovante invalida os dois. */
  leadDeposits: (params: unknown) => ['ranking', 'clientes', params] as const,
  memberDeposits: (params: unknown) => ['ranking', 'clientes', 'por-membro', params] as const,
  links: (params: unknown) => ['links', params] as const,
  attendance: (part: string, params?: unknown) => ['attendance', part, params] as const,
  crypto: (part: string, params?: unknown) => ['crypto', part, params] as const,
  review: (params: unknown) => ['review', params] as const,
};

type QueryParams = Record<string, string | number | boolean | undefined | null>;

/**
 * Invalida tudo que depende de valores: lançar um depósito muda painel,
 * ranking, metas e as listas. É mais barato do que sincronizar cada cache.
 */
function useInvalidateAll() {
  const queryClient = useQueryClient();
  return () => {
    // 'leverage': o comprovante de depósito é o aporte do cliente na alavancagem.
    for (const key of ['dashboard', 'metrics', 'ranking', 'goals', 'leads', 'receipts', 'users', 'attendance', 'crypto', 'leverage', 'review']) {
      void queryClient.invalidateQueries({ queryKey: [key] });
    }
  };
}

/* ------------------------------------------------------------------ reads */

export const useDashboard = (params: QueryParams) =>
  useQuery({
    queryKey: keys.dashboard(params),
    queryFn: () => api.get<Dashboard>('/api/dashboard', params),
    // Trocar o mês da comissão (ou outro filtro) não apaga o painel enquanto carrega.
    placeholderData: (previous) => previous,
  });

/** O bloco do gás no painel: segue o ano e o membro do painel. */
export const useGas = (params: QueryParams) =>
  useQuery({
    queryKey: keys.gas(params),
    queryFn: () => api.get<GasSummary>('/api/dashboard/gas', params),
    placeholderData: (previous) => previous,
  });

/** Ao trocar o filtro, os números anteriores ficam na tela (esmaecidos) até os novos chegarem. */
export const useMetrics = (params: QueryParams) =>
  useQuery({
    queryKey: keys.metrics(params),
    queryFn: () => api.get<Metrics>('/api/metrics', params),
    placeholderData: (previous) => previous,
  });

export const useRanking = (params: QueryParams) =>
  useQuery({
    queryKey: keys.ranking(params),
    queryFn: () => api.get<Ranking>('/api/ranking', params),
  });

/** Os clientes pelo volume depositado no período. */
export const useLeadDepositRanking = (params: QueryParams) =>
  useQuery({
    queryKey: keys.leadDeposits(params),
    queryFn: () => api.get<LeadDepositRanking>('/api/ranking/clientes', params),
  });

/** Os membros do comercial pelo depositado dos clientes deles. */
export const useMemberDepositRanking = (params: QueryParams) =>
  useQuery({
    queryKey: keys.memberDeposits(params),
    queryFn: () => api.get<MemberDepositRanking>('/api/ranking/clientes/por-membro', params),
  });

/** Lista de membros — rota de administrador; para os demais, deixe `enabled` falso. */
export const useUsers = (params: QueryParams = {}, options: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: keys.users(params),
    queryFn: async () => (await api.get<{ users: User[] }>('/api/users', params)).users,
    staleTime: 30_000,
    enabled: options.enabled ?? true,
  });

/**
 * Quem pode ser escolhido como responsável ou creditado: qualquer membro
 * ativo, para o administrador; só a própria pessoa, para os demais.
 */
export function useMemberOptions(): { id: string; name: string }[] {
  const { user, isAdmin } = useAuth();
  const { data } = useUsers({}, { enabled: isAdmin });
  if (isAdmin) return data ?? [];
  return user ? [{ id: user.id, name: user.name }] : [];
}

export const useLeads = (params: QueryParams, options: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: keys.leads(params),
    queryFn: () => api.get<Page<Lead>>('/api/leads', params),
    placeholderData: (previous) => previous,
    enabled: options.enabled ?? true,
  });

export const useReceipts = (params: QueryParams, options: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: keys.receipts(params),
    queryFn: () => api.get<ReceiptPage>('/api/receipts', params),
    placeholderData: (previous) => previous,
    enabled: options.enabled ?? true,
  });

export const useGoals = (params: QueryParams) =>
  useQuery({
    queryKey: keys.goals(params),
    queryFn: () => api.get<GoalsResult>('/api/goals', params),
  });

/**
 * O que a tela precisa saber do servidor: se dá para guardar senha de cliente e
 * enviar foto de perfil, e o endereço oficial da plataforma (a primeira origem de CORS_ORIGINS).
 */
export const useAppConfig = () =>
  useQuery({
    queryKey: ['config'],
    queryFn: () =>
      api.get<{ credentialsEnabled: boolean; photosEnabled: boolean; appUrl: string | null }>('/api/config'),
    staleTime: Infinity,
  });

/** Abre a senha da conta do cliente. Mutação, não consulta: nada fica em cache, e cada abertura é auditada. */
export function useRevealAccountPassword() {
  return useMutation({
    mutationFn: (leadId: string) => api.post<{ password: string }>(`/api/leads/${leadId}/account-password`),
  });
}

/** Os links prontos do membro (ou de outro, para o administrador). */
export const useMemberLinks = (params: QueryParams) =>
  useQuery({
    queryKey: keys.links(params),
    queryFn: () => api.get<MemberLinks>('/api/links', params),
    // Trocar o membro não tira a busca da tela (nem o foco do campo) enquanto os links dele chegam.
    placeholderData: (previous) => previous,
  });

/** Os grupos (plataformas) e os produtos de cada um, para o cadastro de link (administrador). */
export const useLinkGroups = () =>
  useQuery({
    queryKey: keys.links('groups'),
    queryFn: () => api.get<{ items: CheckoutLink[]; groups: LinkGroup[] }>('/api/links/checkouts'),
    select: (data) => data.groups,
  });

/** Quem recebe a oferta (administrador). */
export const useLinkAccess = (key: string) =>
  useQuery({
    queryKey: keys.links({ access: key }),
    queryFn: () => api.get<LinkAccess>(`/api/links/access/${encodeURIComponent(key)}`),
  });

/* ----------------------------------------------------------------- writes */

export function useCreateLead() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (body: unknown) => api.post<{ lead: Lead }>('/api/leads', body),
    onSuccess: invalidate,
  });
}

export function useUpdateLead() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api.patch<{ lead: Lead }>(`/api/leads/${id}`, body),
    onSuccess: invalidate,
  });
}

export function useDeleteLead() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/leads/${id}`),
    onSuccess: invalidate,
  });
}

/** Cadastro com os arquivos: vai em multipart. */
export function useCreateReceipt() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (form: FormData) => api.upload<{ receipt: Receipt }>('/api/receipts', form),
    onSuccess: invalidate,
  });
}

export function useUpdateReceipt() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api.patch<{ receipt: Receipt }>(`/api/receipts/${id}`, body),
    onSuccess: invalidate,
  });
}

/** A conferência das vendas, com a prova do checkout de cada comprovante (só o administrador). */
export const useReview = (params: QueryParams) =>
  useQuery({
    queryKey: keys.review(params),
    queryFn: () => api.get<ReviewResult>('/api/review/sales', params),
    placeholderData: (previous) => previous,
  });

export interface ReviewDecisionInput {
  id: string;
  status: ReviewStatus;
  note?: string;
  /** Na aprovação: o pedido que o comprovante prova. Sem ele, aprova sem pedido. */
  orderKey?: string;
  /** O pedido é de outro membro: passar a venda para quem anexou. */
  transfer?: boolean;
}

/** Vincula ao pedido, aprova sem pedido, reprova (com o motivo) ou devolve para a análise. */
export function useReviewDecision() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, ...body }: ReviewDecisionInput) => api.patch<{ receipt: Receipt }>(`/api/review/receipts/${id}`, body),
    onSuccess: invalidate,
  });
}

/** Dá dono ao pedido pago pelo link direto que ninguém reivindicou (nulo tira). */
export function useAssignOrder() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (body: { orderKey: string; memberId: string | null }) => api.put<void>('/api/review/orders/owner', body),
    onSuccess: invalidate,
  });
}

export function useDeleteReceipt() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/receipts/${id}`),
    onSuccess: invalidate,
  });
}

export function useAddAttachments() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, form }: { id: string; form: FormData }) =>
      api.upload<{ receipt: Receipt }>(`/api/receipts/${id}/attachments`, form),
    onSuccess: invalidate,
  });
}

export function useRemoveAttachment() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, attachmentId }: { id: string; attachmentId: string }) =>
      api.delete<{ receipt: Receipt }>(`/api/receipts/${id}/attachments/${attachmentId}`),
    onSuccess: invalidate,
  });
}

export function useSaveUser() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id ? api.patch<{ user: User }>(`/api/users/${id}`, body) : api.post<{ user: User }>('/api/users', body),
    onSuccess: invalidate,
  });
}

export function useRemoveUser() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: boolean }>(`/api/users/${id}`),
    onSuccess: invalidate,
  });
}

/** Perfil próprio: só o nome — login e papel são decisão de administrador. */
export function useUpdateProfile() {
  return useMutation({
    mutationFn: (body: { name: string }) => api.patch<{ user: User }>('/api/auth/me', body),
  });
}

/**
 * Foto de perfil própria (arquivo já preparado por `preparePhoto`). As listas
 * recarregam para trazer a URL nova da foto em cada avatar.
 */
export function useUploadPhoto() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (photo: File) => {
      const form = new FormData();
      form.append('photo', photo);
      return api.put<{ user: User }>('/api/auth/me/photo', form);
    },
    onSuccess: invalidate,
  });
}

export function useRemovePhoto() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: () => api.delete<{ user: User }>('/api/auth/me/photo'),
    onSuccess: invalidate,
  });
}

/** Troca a própria senha. O servidor encerra todas as sessões abertas em seguida. */
export function useChangePassword() {
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      api.patch<void>('/api/auth/password', body),
  });
}

export function useSaveGoal() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (body: { year: number; month: number; target: number }) => api.put('/api/goals', body),
    onSuccess: invalidate,
  });
}

export function useBulkGoals() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (body: { year: number; goals: { month: number; target: number }[] }) =>
      api.put('/api/goals/bulk', body),
    onSuccess: invalidate,
  });
}

/** Troca o código do membro; os links voltam já com o endereço novo. */
export function useSetMemberCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { code: string; memberId?: string }) => api.put<MemberLinks>('/api/links/code', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['links'] }),
  });
}

/** Links de checkout do administrador: toda mudança chega aos links de todos os membros. */
export function useCreateCheckoutLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CheckoutLinkInput) => api.post<{ checkoutLink: CheckoutLink }>('/api/links/checkouts', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['links'] }),
  });
}

export function useUpdateCheckoutLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: CheckoutLinkInput & { id: string }) =>
      api.patch<{ checkoutLink: CheckoutLink }>(`/api/links/checkouts/${id}`, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['links'] }),
  });
}

export function useRemoveCheckoutLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/links/checkouts/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['links'] }),
  });
}

/** Restringe a oferta aos membros escolhidos, ou volta a dar para todos. */
export function useSetLinkAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, ...body }: { key: string; restricted: boolean; memberIds: string[] }) =>
      api.put<LinkAccess>(`/api/links/access/${encodeURIComponent(key)}`, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['links'] }),
  });
}

/** Relê o catálogo da Cakto agora (administrador). */
export function useRefreshCatalog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<CatalogRefresh>('/api/links/refresh'),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['links'] }),
  });
}

/* ------------------------------------------------------------------ contas da Kirvano */

/**
 * As contas da Kirvano conectadas (administrador). Enquanto `pollWhile`
 * for verdade, relê a cada 10 s — esperando a primeira entrega de uma conta.
 */
export const useKirvanoAccounts = (options: { pollWhile?: (accounts: KirvanoAccount[]) => boolean } = {}) =>
  useQuery({
    queryKey: ['kirvano', 'accounts'],
    queryFn: () => api.get<{ accounts: KirvanoAccount[] }>('/api/kirvano/accounts'),
    select: (data) => data.accounts,
    refetchInterval: (query) => (options.pollWhile?.(query.state.data?.accounts ?? []) ? 10_000 : false),
  });

/** Começa o "entrar com a Kirvano": devolve o endereço da Kirvano para onde o navegador vai. */
export function useConnectKirvano() {
  return useMutation({
    mutationFn: (body: { name: string } | { accountId: string }) => api.post<{ authorizeUrl: string }>('/api/kirvano/accounts/connect', body),
  });
}

/** Lê a conta agora: produtos e vendas. */
export function useSyncKirvanoAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ account: KirvanoAccount }>(`/api/kirvano/accounts/${id}/sync`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['kirvano'] });
      void queryClient.invalidateQueries({ queryKey: ['links'] });
    },
  });
}

export function useUpdateKirvanoAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; active: boolean }) =>
      api.patch<{ account: KirvanoAccount }>(`/api/kirvano/accounts/${id}`, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['kirvano'] }),
  });
}

/* ------------------------------------------------------------------ atendimento da For All Sales */

/** Relatório de atendimento do membro. Ao trocar o período, os números anteriores ficam esmaecidos. */
export const useMemberAttendance = (userId: string | undefined, params: QueryParams) =>
  useQuery({
    queryKey: keys.attendance('member', { userId, ...params }),
    queryFn: () => api.get<MemberAttendance>(`/api/attendance/members/${userId}`, params),
    enabled: Boolean(userId),
    placeholderData: (previous) => previous,
  });

export const useAttendanceTeam = (params: QueryParams, options: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: keys.attendance('team', params),
    queryFn: () => api.get<AttendanceTeamSummary>('/api/attendance/team', params),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
  });

/** Enquanto uma leitura roda, a situação se atualiza sozinha. */
const STATUS_POLL_RUNNING_MS = 3_000;

export const useAttendanceStatus = (options: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: keys.attendance('status'),
    queryFn: () => api.get<AttendanceStatus>('/api/attendance/status'),
    enabled: options.enabled ?? true,
    refetchInterval: (query) => (query.state.data?.running ? STATUS_POLL_RUNNING_MS : false),
  });

export const useFasAttendants = (options: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: keys.attendance('attendants'),
    queryFn: async () => (await api.get<{ attendants: FasAttendant[] }>('/api/attendance/attendants')).attendants,
    enabled: options.enabled ?? true,
  });

export function useSyncFas() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { full?: boolean } = {}) => api.post<{ requested: true }>('/api/attendance/sync', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.attendance('status') }),
  });
}

/** Vincular muda o responsável dos clientes: invalida tudo. */
export function useLinkAttendant() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ attendantId, userId }: { attendantId: number; userId: string | null }) =>
      api.put<{ owners: number }>(`/api/attendance/attendants/${attendantId}/link`, { userId }),
    onSuccess: invalidate,
  });
}

export function useImportAttendants() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (items: Array<{ attendantId: number; login: string; name?: string; email?: string | null }>) =>
      api.post<{ created: ImportedMember[] }>('/api/attendance/attendants/import', { items }),
    onSuccess: invalidate,
  });
}

/* ------------------------------------------------------- vendas em cripto */

export const useCryptoSales = (params: QueryParams = {}) =>
  useQuery({
    queryKey: keys.crypto('list', params),
    queryFn: () => api.get<{ items: CryptoSale[] }>('/api/crypto-sales', params),
    placeholderData: (previous) => previous,
  });

/** As parcelas de hoje e as atrasadas — o que cobrar agora. */
export const useCryptoAgenda = () =>
  useQuery({
    queryKey: keys.crypto('agenda'),
    queryFn: () => api.get<CryptoAgenda>('/api/crypto-sales/agenda'),
  });

/**
 * Toda mudança no carnê mexe no vendido (a parcela paga vira comprovante):
 * painel, ranking, metas e comprovantes se atualizam juntos.
 */
export function useCreateCryptoSale() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (body: CryptoSaleInput) => api.post<{ sale: CryptoSale }>('/api/crypto-sales', body),
    onSuccess: invalidate,
  });
}

export function useUpdateCryptoSale() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; description?: string; exchange?: string | null; note?: string | null; leadId?: string | null }) =>
      api.patch<{ sale: CryptoSale }>(`/api/crypto-sales/${id}`, body),
    onSuccess: invalidate,
  });
}

export function usePayInstallment() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ saleId, installmentId, ...body }: { saleId: string; installmentId: string; date: string; time: string; paidBrl: number; txRef?: string }) =>
      api.post<{ sale: CryptoSale }>(`/api/crypto-sales/${saleId}/installments/${installmentId}/pay`, body),
    onSuccess: invalidate,
  });
}

export function useUnpayInstallment() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ saleId, installmentId }: { saleId: string; installmentId: string }) =>
      api.delete<{ sale: CryptoSale }>(`/api/crypto-sales/${saleId}/installments/${installmentId}/pay`),
    onSuccess: invalidate,
  });
}

export function useSetCryptoSaleState() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'interrupt' | 'resume' }) =>
      api.post<{ sale: CryptoSale }>(`/api/crypto-sales/${id}/${action}`),
    onSuccess: invalidate,
  });
}

export function useDeleteCryptoSale() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/crypto-sales/${id}`),
    onSuccess: invalidate,
  });
}
